"use client";

import { Star } from "lucide-react";
import { useState } from "react";
import { ResponsiveModal } from "@/components/responsive-modal";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { useGetUserStudySetRating } from "@/features/studysets/api/use-get-user-rating";
import { useRateStudySet } from "@/features/studysets/api/use-rate-studyset";
import { cn } from "@/lib/utils";
import { useI18n } from "@/locales/client";

interface RatingDialogProps {
  id: string;
  initialRating?: string;
  totalReviews?: number;
  canEdit: boolean;
}

interface RatingFormProps {
  rating: number;
  setRating: (rating: number) => void;
  hoverRating: number;
  setHoverRating: (rating: number) => void;
  selectedFeedback: string;
  handleFeedbackClick: (feedback: string) => void;
  handleSubmit: () => void;
  isPending: boolean;
}

const RatingForm = ({
  rating,
  setRating,
  hoverRating,
  setHoverRating,
  selectedFeedback,
  handleFeedbackClick,
  handleSubmit,
  isPending,
}: RatingFormProps) => {
  const t = useI18n();
  const negativeOptions = [
    t("ratingFeedback.notEnoughDetail"),
    t("ratingFeedback.poorlyFormatted"),
    t("ratingFeedback.incompleteOrIrrelevant"),
    t("ratingFeedback.other"),
  ];

  const positiveOptions = [
    t("ratingFeedback.comprehensive"),
    t("ratingFeedback.engagingFormat"),
    t("ratingFeedback.goodRefresher"),
    t("ratingFeedback.greatTestResults"),
  ];

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center md:justify-start justify-center gap-1">
        {[1, 2, 3, 4, 5].map((value) => (
          <button
            type="button"
            key={value}
            onClick={() => setRating(value)}
            onMouseEnter={() => setHoverRating(value)}
            onMouseLeave={() => setHoverRating(0)}
            className="p-1 transition-transform hover:scale-110"
          >
            <Star
              className={cn(
                "size-6 transition-colors",
                value <= (hoverRating || rating)
                  ? "fill-yellow-400 stroke-yellow-400"
                  : "stroke-gray-300",
              )}
            />
          </button>
        ))}
      </div>
      {rating > 0 && (
        <>
          <Separator />
          <div className="space-y-6 md:text-start text-center w-full">
            <h4 className="font-medium text-sm">
              {rating <= 3
                ? t("ratingFeedback.whatCouldBeBetter")
                : t("ratingFeedback.whatYouLiked")}
            </h4>
            <div className="flex flex-wrap gap-2">
              {(rating <= 3 ? negativeOptions : positiveOptions).map(
                (option) => (
                  <button
                    type="button"
                    key={option}
                    onClick={() => handleFeedbackClick(option)}
                    className={cn(
                      "rounded-full px-3 py-1 text-xs md:text-sm transition-colors",
                      selectedFeedback === option
                        ? "bg-brand-primary text-primary-foreground"
                        : "bg-secondary hover:bg-secondary/80",
                    )}
                  >
                    {option}
                  </button>
                ),
              )}
            </div>
            <div className="flex flex-col-reverse sm:flex-row sm:space-x-2 sm:justify-end">
              <Button onClick={handleSubmit} disabled={isPending}>
                {isPending
                  ? t("ratingFeedback.submitting")
                  : t("ratingFeedback.submit")}
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const RatingDialog = ({
  id,
  initialRating = "5.0",
  totalReviews = 5,
  canEdit,
}: RatingDialogProps) => {
  const { data: studySetUserRating } = useGetUserStudySetRating(id);
  const [rating, setRating] = useState(studySetUserRating?.rating ?? 0);
  const [hoverRating, setHoverRating] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedFeedback, setSelectedFeedback] = useState<string>(
    studySetUserRating?.feedback ?? "",
  );

  const submitRatingMutation = useRateStudySet({ id });

  const handleSubmit = () => {
    submitRatingMutation.mutate(
      {
        id,
        rating,
        feedback: selectedFeedback,
      },
      {
        onSuccess: () => {
          setIsOpen(false);
        },
      },
    );
  };

  const handleFeedbackClick = (feedback: string) => {
    setSelectedFeedback(feedback);
  };

  return (
    <>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setIsOpen(true)}
        disabled={!canEdit}
        className="inline-flex gap-2 px-1"
      >
        <Star className="size-4 text-[#FDE047] fill-[#FDE047] stroke-yellow-400" />
        <span className="text-muted-foreground text-sm font-medium">
          {Number(initialRating).toFixed(1)} ({totalReviews} reviews)
        </span>
      </Button>
      <ResponsiveModal
        open={isOpen}
        onOpenChange={setIsOpen}
        title="How would you rate this set?"
        form={
          <RatingForm
            rating={rating}
            setRating={setRating}
            hoverRating={hoverRating}
            setHoverRating={setHoverRating}
            selectedFeedback={selectedFeedback}
            handleFeedbackClick={handleFeedbackClick}
            handleSubmit={handleSubmit}
            isPending={submitRatingMutation.isPending}
          />
        }
      />
    </>
  );
};

export default RatingDialog;
